import React, { Component, createRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import '../../css/PDFModal.css';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

class PDFPreviewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      imageSrc: null,
      imageWidth: null,
      imageHeight: null,
      imageX: 100,
      imageY: 100,
      dragging: false,
      resizing: false,
      offsetX: 0,
      offsetY: 0,
      pageWidth: 0,
      pageHeight: 0,
      numPages: null,
      scale: 1,
      currentPage: props.imagePlacementParams?.page || 1,
      pendingImageFile: null,
      imagePlacedFromParams: false,
    };
    this.imageRef = createRef();
    this.pdfPageRef = createRef();
    this.pdfDoc = null;
  }

  componentDidMount() {
    if (this.props.imageForSign) {
      this.setState({ pendingImageFile: this.props.imageForSign });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.imageForSign !== this.props.imageForSign &&
      !this.state.imagePlacedFromParams
    ) {
      const { pageWidth, pageHeight } = this.state;
      if (pageWidth && pageHeight) {
        this.loadImageFromFile(this.props.imageForSign);
        this.setState({ imagePlacedFromParams: true });
      } else {
        this.setState({ pendingImageFile: this.props.imageForSign });
      }
    }

    if (prevState.currentPage !== this.state.currentPage && this.pdfDoc) {
      this.loadCurrentPageSize(this.pdfDoc, this.state.currentPage);
    }
  }

  clamp = (val, min, max) => Math.max(min, Math.min(val, max));

  handleDragStart = (e) => {
    this.setState({
      dragging: true,
      offsetX: e.clientX - this.state.imageX,
      offsetY: e.clientY - this.state.imageY,
    });
    document.addEventListener('mousemove', this.handleDragging);
    document.addEventListener('mouseup', this.handleDragEnd);
  };

  handleDragging = (e) => {
    const { offsetX, offsetY, imageWidth, imageHeight, pageWidth, pageHeight } = this.state;
    const textWidth = this.props.qrText ? 350 + 10 : 0;
    const totalBlockWidth = imageWidth + textWidth;
    const newX = this.clamp(e.clientX - offsetX, 0, pageWidth - totalBlockWidth);
    const newY = this.clamp(e.clientY - offsetY, 0, pageHeight - imageHeight);
    this.setState({ imageX: newX, imageY: newY });
  };

  handleDragEnd = () => {
    this.setState({ dragging: false });
    document.removeEventListener('mousemove', this.handleDragging);
    document.removeEventListener('mouseup', this.handleDragEnd);
  };

  handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({
      resizing: true,
      resizeStartWidth: this.state.imageWidth,
      resizeStartHeight: this.state.imageHeight,
      resizeStartX: e.clientX,
    });
    document.addEventListener('mousemove', this.handleResizing);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizing = (e) => {
    const {
      imageX,
      imageY,
      resizeStartX,
      resizeStartWidth,
      resizeStartHeight,
      pageWidth,
      pageHeight,
    } = this.state;

    const deltaX = e.clientX - resizeStartX;
    const scale = (resizeStartWidth + deltaX) / resizeStartWidth;

    let newWidth = resizeStartWidth * scale;
    let newHeight = resizeStartHeight * scale;

    if (newWidth + imageX > pageWidth) {
      newWidth = pageWidth - imageX;
      newHeight = (resizeStartHeight / resizeStartWidth) * newWidth;
    }
    if (newHeight + imageY > pageHeight) {
      newHeight = pageHeight - imageY;
      newWidth = (resizeStartWidth / resizeStartHeight) * newHeight;
    }

    if (newWidth > 20 && newHeight > 20) {
      this.setState({ imageWidth: newWidth, imageHeight: newHeight });
    }
  };

  handleResizeEnd = () => {
    this.setState({ resizing: false });
    document.removeEventListener('mousemove', this.handleResizing);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  };

  handleDocumentLoadSuccess = (pdf) => {
    this.setState({ numPages: pdf.numPages }, () => {
      const pageToLoad = this.state.currentPage || 1;
      this.pdfDoc = pdf;
      this.loadCurrentPageSize(pdf, pageToLoad);
    });
  };

  loadCurrentPageSize = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1 });

    const containerPadding = 40;
    const maxWidth = window.innerWidth - containerPadding;
    const maxHeight = window.innerHeight - 150;

    const widthRatio = maxWidth / viewport.width;
    const heightRatio = maxHeight / viewport.height;
    const newScale = Math.min(widthRatio, heightRatio, 1);

    const newPageWidth = viewport.width * newScale;
    const newPageHeight = viewport.height * newScale;

    this.setState(
      {
        scale: newScale,
        pageWidth: newPageWidth,
        pageHeight: newPageHeight,
      },
      () => {
        const { imageSrc, imageWidth, imageHeight } = this.state;

        if (imageSrc && imageWidth && imageHeight) {
          const centerX = (newPageWidth - imageWidth) / 2;
          const centerY = (newPageHeight - imageHeight) / 2;
          this.setState({ imageX: centerX, imageY: centerY });
        }

        if (this.state.pendingImageFile && !this.state.imagePlacedFromParams) {
          requestAnimationFrame(() => {
            this.loadImageFromFile(this.state.pendingImageFile);
            this.setState({
              pendingImageFile: null,
              imagePlacedFromParams: true,
            });
          });
        }
      }
    );
  };

  loadImageFromFile = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const params = this.props.imagePlacementParams;
        const { pageWidth, pageHeight } = this.state;

        const margin = 20;
        const maxWidth = pageWidth - margin * 2;
        const maxHeight = pageHeight - margin * 2;

        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;

        let textWidth = 0;
        let textHeight = 0;
        if (this.props.qrText) {
          const fontSize = 12;
          const lineHeight = fontSize * 1.3;
          const lines = this.props.qrText.split(/\r?\n/);
          textHeight = lines.length * lineHeight;
          textWidth = 350;
        }

        let scaleImage = 1;
        const blockWidth = naturalWidth + (textWidth > 0 ? textWidth + 10 : 0);
        const blockHeight = Math.max(naturalHeight, textHeight);
        if (blockWidth > maxWidth || blockHeight > maxHeight) {
          const widthRatio = maxWidth / blockWidth;
          const heightRatio = maxHeight / blockHeight;
          scaleImage = Math.min(widthRatio, heightRatio);
        }

        const scaledWidth = naturalWidth * scaleImage;
        const scaledHeight = naturalHeight * scaleImage;

        let imageX, imageY, imageWidth, imageHeight;

        if (params) {
          const scaleFactor = this.state.scale;
          imageX = params.x * scaleFactor;
          imageY = pageHeight - params.y * scaleFactor;
          imageWidth = params.width * scaleFactor;
          imageHeight = params.height * scaleFactor;
        } else {
          imageWidth = scaledWidth;
          imageHeight = scaledHeight;
          const totalBlockWidth = scaledWidth + (textWidth > 0 ? textWidth * scaleImage + 10 : 0);
          const totalBlockHeight = Math.max(scaledHeight, textHeight * scaleImage);

          imageX = (pageWidth - totalBlockWidth) / 2;
          imageY = (pageHeight - totalBlockHeight) / 2;
        }

        this.setState({
          imageSrc: e.target.result,
          imageX,
          imageY,
          imageWidth,
          imageHeight,
          imagePlacedFromParams: !!params,
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  generateResizedImage = () => {
    const imageFile = this.props.imageForSign;
    const { qrText, base64Data } = this.props;
    const {
      imageWidth,
      imageHeight,
      imageX,
      imageY,
      pageHeight,
      currentPage,
      scale,
    } = this.state;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = imageWidth;
      canvas.height = imageHeight;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, imageWidth, imageHeight);

      canvas.toBlob((blob) => {
        const resizedFile = new File([blob], imageFile.name, {
          type: imageFile.type,
          lastModified: Date.now(),
        });

        this.props.onImageProcessed({
          file: resizedFile,
          coords: {
            page: currentPage,
            x: imageX / scale,
            y: (pageHeight - imageY) / scale,
            width: imageWidth / scale,
            height: imageHeight / scale,
          },
          textData: {
            qrText: qrText,
            base64Data: base64Data,
            currentPage: currentPage,
            imageX: imageX,
            imageY: imageY,
            imageWidth: imageWidth,
            imageHeight: imageHeight,
            scale: scale
          }
        });
      }, imageFile.type);
    };

    img.src = URL.createObjectURL(imageFile);

    // const imageFile = this.props.imageForSign;
    // const { qrText, base64Data, onImageProcessed } = this.props;
    // const { imageWidth, imageHeight, imageX, imageY, pageHeight, currentPage, scale } = this.state;

    // const img = new Image();
    // img.onload = () => {
    //   const textPadding = 10;
    //   const textMaxWidth = 350;
    //   const fontSize = 11;
    //   const lineHeight = fontSize * 1.3;
    //   const textColor = "#000";
    //   const background = "#fff";

    //   let textLines = [];
    //   let totalTextHeight = 0;
    //   if (qrText) {
    //     textLines = qrText.split(/\r?\n/);
    //     totalTextHeight = textLines.length * lineHeight;
    //   }

    //   const totalWidth = imageWidth + (qrText ? textMaxWidth + textPadding : 0);
    //   const totalHeight = Math.max(imageHeight, totalTextHeight);

    //   const scaleFactor = 2;
    //   const canvas = document.createElement("canvas");
    //   canvas.width = totalWidth * scaleFactor;
    //   canvas.height = totalHeight * scaleFactor;

    //   const ctx = canvas.getContext("2d");
    //   ctx.scale(scaleFactor, scaleFactor);

    //   ctx.fillStyle = background;
    //   ctx.fillRect(0, 0, totalWidth, totalHeight);

    //   ctx.imageSmoothingEnabled = true;
    //   ctx.imageSmoothingQuality = "high";
    //   ctx.drawImage(img, 0, 0, imageWidth, imageHeight);

      // if (qrText) {
      //   ctx.imageSmoothingEnabled = false;
      //   ctx.fillStyle = textColor;
      //   ctx.strokeStyle = "#000";
      //   ctx.lineWidth = 0.2;
      //   ctx.font = `${fontSize}px Verdana, Geneva, sans-serif`;
      //   ctx.textAlign = "left";
      //   ctx.textBaseline = "alphabetic";

      //   const baselineOffset = fontSize * 0.25;
      //   const qrCenterY = imageHeight / 2;
      //   let startY = qrCenterY - totalTextHeight / 2 + baselineOffset;
      //   const x = imageWidth + textPadding;

      //   for (const block of textLines) {
      //     const words = block.split(/\s+/);
      //     let line = "";
      //     for (let i = 0; i < words.length; i++) {
      //       const testLine = line + words[i] + " ";
      //       const testWidth = ctx.measureText(testLine).width;
      //       if (testWidth > textMaxWidth && i > 0) {
      //         ctx.strokeText(line.trim(), x, startY);
      //         ctx.fillText(line.trim(), x, startY);
      //         line = words[i] + " ";
      //         startY += lineHeight;
      //       } else {
      //         line = testLine;
      //       }
      //     }
      //     if (line) {
      //       ctx.strokeText(line.trim(), x, startY);
      //       ctx.fillText(line.trim(), x, startY);
      //       startY += lineHeight;
      //     }
      //   }

      //   ctx.imageSmoothingEnabled = true;
      // }

    //   const finalCanvas = document.createElement("canvas");
    //   finalCanvas.width = totalWidth;
    //   finalCanvas.height = totalHeight;
    //   const finalCtx = finalCanvas.getContext("2d");
    //   finalCtx.drawImage(canvas, 0, 0, totalWidth, totalHeight);

    //   finalCanvas.toBlob((blob) => {
    //     const mergedFile = new File([blob], imageFile.name, {
    //       type: imageFile.type || "image/png",
    //       lastModified: Date.now(),
    //     });

    //     onImageProcessed({
    //       file: mergedFile,
    //       coords: {
    //         page: currentPage,
    //         x: imageX / scale,
    //         y: (pageHeight - imageY) / scale,
    //         width: totalWidth / scale,
    //         height: totalHeight / scale,
    //       },
    //     });

    //     // this.addToPDFWithFont(mergedFile, qrText, base64Data, currentPage, imageX, imageY, imageWidth, imageHeight, scale);
    //   }, imageFile.type || "image/png");
    // };

    // img.src = URL.createObjectURL(imageFile);
  };

  render() {
    const { base64Data, onClose } = this.props;
    const {
      imageSrc,
      imageX,
      imageY,
      imageWidth,
      imageHeight,
      currentPage,
      numPages,
      scale,
    } = this.state;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content-pdf" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>×</button>

          <div className="pdf-borders" style={{ position: 'relative' }} ref={this.pdfPageRef}>
            <Document
              file={base64Data}
              onLoadError={(err) => alert('Помилка PDF: ' + err.message)}
              onLoadSuccess={this.handleDocumentLoadSuccess}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>

            {imageSrc && (
              <div
                ref={this.imageRef}
                id="qr-block"
                style={{
                  position: 'absolute',
                  top: imageY,
                  left: imageX,
                  border: '1px dashed #000',
                  cursor: 'move',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  background: '#fff'
                }}
                onMouseDown={this.handleDragStart}
              >
                <img
                  src={imageSrc}
                  alt="QR"
                  style={{
                    width: imageWidth,
                    height: imageHeight,
                    pointerEvents: 'none',
                    display: 'block',
                  }}
                  draggable={false}
                />

                {this.props.qrText && (
                  <div
                    ref={(el) => (this.qrTextRef = el)}
                    style={{
                      marginLeft: 10,
                      width: 350,
                      fontSize: 12,
                      color: '#000',
                      lineHeight: '1.3em',
                      whiteSpace: 'pre-line',
                      textAlign: 'left',
                    }}
                  >
                    {this.props.qrText}
                  </div>
                )}

                {this.props.resizableImage && (
                  <div
                    onMouseDown={this.handleResizeStart}
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: 0,
                      width: 10,
                      height: 10,
                      background: 'blue',
                      cursor: 'nwse-resize',
                    }}
                  />
                )}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 7,
              marginBottom: 7,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <button className="btn btn-default" onClick={this.generateResizedImage}>
              Зберегти позицію та зображення
            </button>
            <button
              className="btn btn-default"
              onClick={() =>
                this.setState((prev) => ({
                  currentPage: Math.max(1, prev.currentPage - 1),
                }))
              }
              disabled={currentPage <= 1}
            >
              Назад
            </button>
            <span>
              Сторінка {currentPage} з {numPages}
            </span>
            <button
              className="btn btn-default"
              onClick={() =>
                this.setState((prev) => ({
                  currentPage: Math.min(numPages, prev.currentPage + 1),
                }))
              }
              disabled={currentPage >= numPages}
            >
              Вперед
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default PDFPreviewer;